import { enumValue, isArray, isNotEmpty, isObject, isString, removeUndefined, toArray } from "@churchofjesuschrist/maps-utils";
import { AreaUnitType, Filter, LengthUnitType, Position, TimeUnitType } from "@churchofjesuschrist/maps-models";
import Convertible from "./Convertible";
export const serializeIdentifiables = (values, prop = "type") => {
  return toArray(serializeIdentifiable(values, prop));
};
export const serializeIdentifiable = (value, prop = "type") => {
  if (isArray(value)) {
    return value.map(entry => serializeIdentifiable(entry, prop)).filter(isNotEmpty);
  } else if (isObject(value)) {
    let parts = [];
    let type = value[prop];
    if (type) {
      parts.push(type);
    }
    parts.push(value.id);
    return parts.join(":");
  }
  return value;
};
export const deserializeIdentifiable = (value, prop = "type") => {
  if (isArray(value)) {
    return value.map(entry => deserializeIdentifiable(entry, prop)).filter(isNotEmpty);
  } else if (isString(value)) {
    let parts = value.split(/\s*[:/]\s*/g);
    if (parts.length >= 2) {
      return {
        id: parts[1],
        [prop]: parts[0].toUpperCase()
      };
    } else {
      return {
        id: value
      };
    }
  }
  return value;
};
export const compileIdentifiable = (id, type = null, prop = "type") => {
  if (isObject(id)) {
    let value = type ? {
      ...id,
      [prop]: type
    } : id;
    return serializeIdentifiable(value, prop);
  } else {
    let value = {
      id,
      [prop]: type
    };
    return serializeIdentifiable(value, prop);
  }
};
export const locationsOperation = (layers, ids, filters) => {
  return {
    layers: toArray(layers),
    ids: serializeIdentifiables(ids),
    filters: serializeFilters(filters)
  };
};
export const refinableOperation = (layers, ids, filters, limit, start, children, associated, related) => {
  return {
    ...locationsOperation(layers, ids, filters),
    limit,
    start,
    children: toArray(children),
    associated: toArray(associated),
    related: toArray(related)
  };
};
export const deserializeLocation = location => {
  if (isArray(location)) {
    return location.map(deserializeLocation, this).filter(isNotEmpty);
  } else if (isObject(location)) {
    return removeUndefined({
      key: serializeIdentifiable(location),
      ...location,
      facility: deserializeLocation(location.facility),
      parent: deserializeLocation(location.parent),
      children: deserializeLocation(location.children),
      associated: deserializeLocation(location.associated)
    });
  }
  return location;
};
export const deserializeCluster = cluster => {
  if (isArray(cluster)) {
    return cluster.map(deserializeCluster).filter(isNotEmpty);
  } else if (isObject(cluster)) {
    return removeUndefined({
      ...cluster,
      locations: deserializeIdentifiable(cluster.locations)
    });
  }
  return cluster;
};
export const serializeWaypoints = (waypoints, converter) => {
  if (isArray(waypoints)) {
    if (converter) {
      let coordinates = converter.convertTo(waypoints, Convertible.POSITION);
      if (Position.isPosition(coordinates)) {
        return [serializeWaypoint(waypoints, converter)];
      }
    }
    return waypoints.map(waypoint => serializeWaypoint(waypoint, converter));
  }
  return toArray(serializeWaypoint(waypoints, converter));
};
export const serializeWaypoint = (waypoint, converter) => {
  let via = waypoint?.via;
  let value = null;
  if (converter) {
    let coordinates = converter.convertTo(waypoint, Convertible.POSITION);
    if (Position.isPosition(coordinates)) {
      value = coordinates.join(",");
    }
  }
  if (!value && isObject(waypoint) && !isArray(waypoint)) {
    if (waypoint.value) {
      value = waypoint.value;
    } else if (waypoint.id) {
      value = serializeIdentifiable(waypoint);
    }
  }
  if (value) {
    return value + (via ? "|via" : "");
  }
  return waypoint;
};
export const serializeFilters = filters => {
  return toArray(serializeFilter(filters));
};
export const serializeFilter = filter => {
  if (isArray(filter)) {
    return filter.map(serializeFilter).filter(isNotEmpty);
  } else if (isObject(filter)) {
    return Filter.serialize(filter);
  }
  return filter;
};
export const serializeStyles = styles => {
  return toArray(serializeStyle(styles));
};
export const serializeStyle = style => {
  if (isArray(style)) {
    return style.map(serializeStyle).filter(isNotEmpty);
  } else if (isObject(style)) {
    let params = [];
    if (style.fill) {
      params.push("f:" + serializeColor(style.fill));
    }
    if (style.stroke) {
      params.push("s:" + serializeColor(style.stroke));
    }
    if (isNotEmpty(style.weight)) {
      params.push("w:" + style.weight);
    }
    if (style.padding) {
      params.push("p:" + style.padding);
    }
    if (style.label) {
      params.push("l:" + serializeLabel(style.label));
    }
    if (style.types) {
      params.push("t:" + serializeObjectTypes(style.types));
    }
    return params.join("|");
  } else if (isString(style)) {
    return serializeColor(style);
  }
  return style;
};
export const serializeObjectTypes = types => {
  if (isArray(types)) {
    return types.join(";");
  }
  return types;
};
export const serializeColor = color => {
  if (color) {
    return color.replace(/\s*,\s*/g, " ");
  }
  return color;
};
export const serializeLabel = label => {
  if (isObject(label)) {
    let params = [];
    if (label.expression) {
      params.push("e:" + serializeExpression(label.expression));
    }
    if (label.color) {
      params.push("c:" + serializeColor(label.color));
    }
    if (label.font) {
      params.push("f:" + serializeFont(label.font));
    }
    return params.join("$");
  }
  return label;
};
export const serializeExpression = expression => {
  if (expression) {
    //TODO: escape ",| inside
    return '"' + expression + '"';
  }
  return null;
};
export const serializeFont = font => {
  if (isObject(font)) {
    return [font.style, font.weight, font.decoration, font.size, font.family].filter(isNotEmpty).join(" ");
  }
  return font;
};
export const serializeIntersections = intersections => {
  return toArray(serializeIntersection(intersections));
};
export const serializeIntersection = intersection => {
  if (isArray(intersection)) {
    return intersection.map(serializeIntersection).filter(isNotEmpty);
  } else if (isObject(intersection)) {
    return {
      ...intersection,
      connections: serializeIntersectionConnections(intersection.connections)
    };
  }
  return intersection;
};
export const serializeIntersectionConnections = connections => {
  return toArray(serializeIntersectionConnection(connections));
};
export const serializeIntersectionConnection = connection => {
  if (isArray(connection)) {
    return connection.map(serializeIntersectionConnection).filter(isNotEmpty);
  } else if (isObject(connection)) {
    return {
      ...connection,
      distance: serializeMeasurement(connection.distance)
    };
  }
  return connection;
};
export const serializeMeasurements = measurements => {
  return toArray(serializeMeasurement(measurements));
};
export const serializeMeasurement = measurement => {
  if (isArray(measurement)) {
    return measurement.map(serializeMeasurement).filter(isNotEmpty);
  } else if (isObject(measurement)) {
    let type = enumValue(measurement.type, LengthUnitType, AreaUnitType, TimeUnitType);
    return `${measurement.value}${type || ""}`;
  }
  return measurement;
};